import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, FileText, Calendar, User, MapPin, Clock } from 'lucide-react';
import { PendingAppointment } from '../../services/AppointmentStatusService';
import { AppointmentStatusService } from '../../services/AppointmentStatusService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildTenantPath } from '../../utils/tenantSlug';
import { AbsenceDecisionModal } from './AbsenceDecisionModal';
import type { AbsenceDecisionPreview } from '../../services/AppointmentStatusService';

interface AppointmentStatusModalProps {
  appointment: PendingAppointment;
  userId: string;
  onClose: () => void;
  onResolved: () => void;
  forcedAction?: 'no_show' | 'cancelled' | null;
}

export const AppointmentStatusModal: React.FC<AppointmentStatusModalProps> = ({
  appointment,
  userId,
  onClose,
  onResolved,
  forcedAction = null,
}) => {
  const navigate = useNavigate();
  const { selectedClient } = useAuth();
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [absencePreview, setAbsencePreview] = useState<AbsenceDecisionPreview | null>(null);
  const [showAbsenceDecision, setShowAbsenceDecision] = useState(false);
  const [absenceIdempotencyKey, setAbsenceIdempotencyKey] = useState('');

  const [auditTrail, setAuditTrail] = useState<Awaited<ReturnType<typeof AppointmentStatusService.getResolutionAuditTrail>>>([]);
  const [loadingAuditTrail, setLoadingAuditTrail] = useState(false);
  const isNotesRequired = forcedAction === 'no_show' || forcedAction === 'cancelled';
  const shouldShowCompletedActions = !forcedAction;
  const shouldShowNoShowAction = !forcedAction || forcedAction === 'no_show';
  const shouldShowCancelledAction = !forcedAction || forcedAction === 'cancelled';
  const modalTitle =
    forcedAction === 'cancelled'
      ? 'Confirmar Cancelamento do Agendamento'
      : forcedAction === 'no_show'
        ? 'Registrar Falta do Agendamento'
        : 'Confirmar Status do Agendamento';


  const getMedicalRecordPath = () => {
    const tenantSlug = selectedClient?.clientSlug;

    switch (selectedClient?.userRole) {
      case 'admin':
      case 'interno':
        return buildTenantPath('/admin/prontuario', tenantSlug);
      case 'fisioterapeuta':
        return buildTenantPath('/fisioterapeuta/prontuario', tenantSlug);
      default:
        return null;
    }
  };

  useEffect(() => {
    loadPatientFinancialInfo();
  }, [appointment.patientId, appointment.clientId, appointment.id, appointment.start]);

  useEffect(() => {
    loadAuditTrail();
  }, [appointment.id]);

  const loadPatientFinancialInfo = async () => {
    if (!appointment.patientId || !appointment.clientId) {
      setAbsencePreview(null);
      return;
    }

    try {
      const preview = await AppointmentStatusService.previewAbsenceDecision(appointment.clientId, appointment.id);
      setAbsencePreview(preview);
    } catch (error) {
      console.error('Erro ao carregar informações financeiras:', error);
    }
  };

  const loadAuditTrail = async () => {
    try {
      setLoadingAuditTrail(true);
      const entries = await AppointmentStatusService.getResolutionAuditTrail(appointment.id);
      setAuditTrail(entries);
    } catch (error) {
      console.error('Erro ao carregar auditoria do agendamento:', error);
    } finally {
      setLoadingAuditTrail(false);
    }
  };

  const formatAuditAction = (action: string) => {
    switch (action) {
      case 'completed':
        return 'Concluído';
      case 'cancelled':
        return 'Cancelado';
      case 'no_show':
        return 'Falta';
      case 'scheduled':
        return 'Voltou para agendado';
      case 'no_evaluated':
        return 'Não avaliado';
      case 'no_billed':
        return 'Concluído sem faturamento';
      default:
        return action;
    }
  };

  const handleCompleted = async () => {
    setLoading(true);
    try {
      const medicalRecordPath = getMedicalRecordPath();
      if (!medicalRecordPath) {
        toast.error('O perfil atual não possui acesso configurado ao prontuário.');
        return;
      }

      toast.info('Abrindo página de prontuário para registrar evolução...');
      navigate(medicalRecordPath, {
        state: {
          preSelectedPatient: appointment.patientId,
          preSelectedAppointment: appointment.id
        }
      });
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao abrir página de evolução');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletedAlready = async () => {
    if (!appointment.patientId) {
      toast.error('Paciente não identificado neste agendamento');
      return;
    }

    const confirmMessage = 'Tem certeza que a evolução já foi registrada? O sistema não encontrou registro vinculado a este agendamento.';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const evolutionId = `manual-${appointment.id}-${Date.now()}`;
      await AppointmentStatusService.resolveAsCompleted(
        appointment.id,
        evolutionId,
        userId,
        notes || 'Marcado manualmente como concluído - evolução registrada anteriormente'
      );

      toast.success('Agendamento marcado como concluído');
      onResolved();
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao marcar como concluído');
    } finally {
      setLoading(false);
    }
  };

  const handleNoShow = async () => {
    if (!appointment.patientId || !appointment.clientId) {
      toast.error('Paciente ou clínica não identificado neste agendamento');
      return;
    }

    const trimmedNotes = notes.trim();
    if (!trimmedNotes) {
      toast.error('Informe o motivo da falta para registrar o no-show.');
      return;
    }

    const validation = await AppointmentStatusService.validateCanMarkAsNoShow(appointment.id);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setLoading(true);
    try {
      const preview = absencePreview ?? await AppointmentStatusService.previewAbsenceDecision(appointment.clientId, appointment.id);
      setAbsencePreview(preview);
      setAbsenceIdempotencyKey(crypto.randomUUID());
      setShowAbsenceDecision(true);
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Não foi possível avaliar a política de faltas deste atendimento.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelled = async () => {
    const trimmedNotes = notes.trim();
    if (!trimmedNotes) {
      toast.error('Informe o motivo do cancelamento para concluir esta ação.');
      return;
    }

    const confirmMessage = 'Confirmar cancelamento deste agendamento?';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await AppointmentStatusService.resolveAsCancelled(appointment.id, userId, trimmedNotes);
      toast.success('Agendamento cancelado');
      onResolved();
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao cancelar agendamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{modalTitle}</h2>
          <button
          onClick={() => {
            setShowAbsenceDecision(false);
            setAbsencePreview(null);
            onClose();
          }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-blue-900">
              <User className="w-5 h-5" />
              <span className="font-semibold text-lg">{appointment.patientName}</span>
            </div>

            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span>{format(appointment.start, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
              </div>

              {appointment.location && (
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>{appointment.location}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>Há {Math.floor(appointment.hoursSincePassed / 24)}d {appointment.hoursSincePassed % 24}h</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observações{isNotesRequired ? ' *' : ''}
            </label>
            <p className="mb-2 text-xs text-gray-500">
              {isNotesRequired
                ? 'Obrigatório para concluir esta ação.'
                : 'Obrigatório para registrar falta ou cancelamento.'}
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Adicione observações sobre este atendimento..."
              disabled={loading}
            />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">O que aconteceu com este agendamento?</p>

            {shouldShowCompletedActions && (
              <button
                onClick={handleCompleted}
                disabled={loading}
                className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 border-2 border-green-300 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 text-white p-2 rounded-lg group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-green-900">Atendido - Registrar Evolução Agora</p>
                    <p className="text-sm text-green-700">Abrir página de prontuário para registrar</p>
                  </div>
                </div>
                <CheckCircle className="w-6 h-6 text-green-600" />
              </button>
            )}

            {shouldShowCompletedActions && (
              <button
                onClick={handleCompletedAlready}
                disabled={loading}
                className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500 text-white p-2 rounded-lg group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-blue-900">Atendido - Evolução Já Registrada</p>
                    <p className="text-sm text-blue-700">Apenas marcar como concluído</p>
                  </div>
                </div>
              </button>
            )}

            {shouldShowNoShowAction && (
              <button
                onClick={handleNoShow}
                disabled={loading}
                className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 border-2 border-red-300 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-red-500 text-white p-2 rounded-lg group-hover:scale-110 transition-transform">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-red-900">Paciente Faltou (No-Show)</p>
                    <p className="text-sm text-red-700">
                      {absencePreview ? `Falta #${absencePreview.defaultAbsenceNumber} — Avaliar política de cobrança` : 'Avaliar política de cobrança'}
                    </p>
                  </div>
                </div>
              </button>
            )}

            {shouldShowCancelledAction && (
              <button
                onClick={handleCancelled}
                disabled={loading}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 border-2 border-gray-300 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-gray-500 text-white p-2 rounded-lg group-hover:scale-110 transition-transform">
                    <XCircle className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Cancelado</p>
                    <p className="text-sm text-gray-700">Agendamento foi cancelado</p>
                  </div>
                </div>
              </button>
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="mb-3">
              <p className="text-sm font-semibold text-slate-900">Histórico de auditoria</p>
              <p className="text-xs text-slate-600">
                O motivo e a ação ficam registrados por evento neste agendamento.
              </p>
            </div>

            {loadingAuditTrail ? (
              <p className="text-sm text-slate-500">Carregando histórico...</p>
            ) : auditTrail.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum registro de auditoria ainda para este agendamento.</p>
            ) : (
              <div className="space-y-3">
                {auditTrail.map((entry) => (
                  <div key={entry.id} className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <span className="font-semibold text-slate-900">{formatAuditAction(entry.action)}</span>
                      <span className="text-slate-500">
                        {format(new Date(entry.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                      <span className="text-slate-600">
                        {entry.actorDisplayName || entry.actorEmail || entry.actorUserId || 'Usuário não identificado'}
                      </span>
                    </div>
                    {(entry.previousStatus || entry.newStatus) && (
                      <p className="mt-1 text-xs text-slate-500">
                        {entry.previousStatus || 'sem status'} → {entry.newStatus || 'sem status'}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-slate-700">
                      {entry.notes?.trim() || 'Sem motivo/observação informado.'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
          <button
            onClick={() => {
              setShowAbsenceDecision(false);
              setAbsencePreview(null);
              onClose();
            }}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>

    <AbsenceDecisionModal
      isOpen={showAbsenceDecision}
      preview={absencePreview}
      loading={loading}
      onConfirm={async (absenceType, optionId) => {
        setLoading(true);
        try {
          const result = await AppointmentStatusService.resolveAsNoShow({
            clientId: appointment.clientId || '',
            appointmentId: appointment.id,
            absenceType,
            optionId,
            notes,
            idempotencyKey: absenceIdempotencyKey || crypto.randomUUID(),
          });

          if (absenceType === 'justified') {
            toast.success('Falta justificada registrada sem cobrança.');
          } else if (result.financialImpact !== 'none') {
            toast.success('Falta registrada e efeito financeiro aplicado.');
          } else if (result.status === 'pending') {
            toast.success('Falta registrada. O impacto financeiro ficou pendente de processamento.');
          } else {
            toast.success('Falta registrada sem impacto financeiro.');
          }

          setShowAbsenceDecision(false);
          setAbsencePreview(null);
          onResolved();
          onClose();
        } catch (error) {
          console.error('Erro ao registrar falta:', error);
          toast.error(error instanceof Error ? error.message : 'Erro ao registrar falta');
        } finally {
          setLoading(false);
        }
      }}
      onCancel={() => setShowAbsenceDecision(false)}
    />
    </>
  );
};
